import { useEffect, useState } from "react";
import { PREVIEW_MODE } from "./PreviewNoPHIBanner";
import { useAuth } from "../context/AuthContext";

/**
 * ARIA Diagnostic Panel — P0 acceptance-testing helper (2026-01)
 *
 * Enabled only when ALL of these hold:
 *   1. `PREVIEW_MODE` (`REACT_APP_PREVIEW_MODE=1` at build time),
 *   2. The URL contains `?ariaDebug=1`, AND
 *   3. The current authenticated user has `is_admin=true`
 *      (Mobile ARIA Phase 1: ordinary clinicians must never see it).
 *
 * Shows a compact latency + environment readout so a clinician can validate
 * P0 speed goals on a physical iPhone / iPad / desktop without opening
 * browser dev tools.
 *
 * SAFETY: This panel deliberately shows only counters, error codes, and the
 * display-mode label. It NEVER renders:
 *   • the transcript
 *   • the spoken question
 *   • any response text
 *   • patient info or any other clinical content
 * The panel reads `window.__ariaLatency` numeric fields only.
 */

function detectDisplayMode() {
  try {
    // iOS PWA
    if (window.navigator && window.navigator.standalone === true) return "iOS PWA";
    // Android/desktop PWA
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return "Installed PWA";
    if (window.matchMedia && window.matchMedia("(display-mode: fullscreen)").matches) return "Fullscreen PWA";
  } catch (_) { /* no-op */ }
  return "Browser tab";
}

function detectBrowser() {
  try {
    const ua = (navigator && navigator.userAgent) || "";
    // Order matters — Edge and Chrome both contain "Safari" in UA.
    if (/Edg\//.test(ua)) return "Edge";
    if (/CriOS/.test(ua)) return "Chrome (iOS)";
    if (/FxiOS/.test(ua)) return "Firefox (iOS)";
    if (/EdgiOS/.test(ua)) return "Edge (iOS)";
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Version\/[\d.]+ Safari/.test(ua)) return "Safari";
    return "Unknown";
  } catch (_) { return "Unknown"; }
}

function detectDevice() {
  try {
    const ua = (navigator && navigator.userAgent) || "";
    if (/iPhone/.test(ua)) return "iPhone";
    if (/iPad/.test(ua)) return "iPad";
    // iPadOS 13+ reports as Mac — disambiguate via touch support.
    if (/Macintosh/.test(ua) && "ontouchend" in document) return "iPad";
    if (/Android/.test(ua)) return "Android";
    if (/Macintosh/.test(ua)) return "Mac";
    if (/Windows/.test(ua)) return "Windows";
    return "Unknown";
  } catch (_) { return "Unknown"; }
}

function fmt(ms) {
  if (ms === undefined || ms === null) return "—";
  return `${Math.round(ms)} ms`;
}

export default function AriaDebugPanel() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [snap, setSnap] = useState({});

  useEffect(() => {
    if (!PREVIEW_MODE) return;
    // Match `?ariaDebug=1` (case-sensitive to keep the flag stable in tests).
    const on = /[?&]ariaDebug=1(?:&|$)/.test(window.location.search);
    setEnabled(on);
    if (!on) return;
    const iv = setInterval(() => {
      const l = (window.__ariaLatency || {});
      const d = (window.__ariaDiag || {});
      // Copy the numeric fields + a bounded slice of the event trace.
      // The trace itself contains only { event: string, t: number }
      // pairs — verified at push time in AriaContext.
      const rawTrace = Array.isArray(d.trace) ? d.trace : [];
      const trace = rawTrace.slice(-10).map((e) => ({
        event: String(e && e.event || ""),
        t: Number(e && e.t) || 0,
      }));
      setSnap({
        tap_to_listening_ms: l.tap_to_listening_ms,
        listening_to_speech_end_ms: l.listening_to_speech_end_ms,
        tts_duration_ms: l.tts_duration_ms,
        tts_start: l.tts_start,
        tts_end: l.tts_end,
        recognition_error_code: l.recognition_error_code,
        readiness_timeout_ms: d.readiness_timeout_ms,
        max_keep_alive_restarts: d.max_keep_alive_restarts,
        trace,
      });
    }, 250);
    return () => clearInterval(iv);
  }, []);

  // Mobile ARIA Phase 1: even with the preview flag + query string, the
  // panel MUST stay hidden for non-admin users. Admin status is trusted
  // from the JWT-backed /auth/me response.
  if (!PREVIEW_MODE || !enabled) return null;
  if (!user || !user.is_admin) return null;

  const device = detectDevice();
  const browser = detectBrowser();
  const mode = detectDisplayMode();

  return (
    <div
      data-testid="aria-debug-panel"
      role="status"
      aria-label="ARIA diagnostic panel"
      style={{
        position: "fixed",
        top: "calc(env(safe-area-inset-top, 0px) + 8px)",
        right: "8px",
        zIndex: 60,
        maxWidth: "220px",
        pointerEvents: "none",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
    >
      <div
        style={{
          background: "rgba(20, 20, 22, 0.86)",
          border: "1px solid rgba(214, 176, 108, 0.4)",
          borderRadius: "8px",
          padding: "8px 10px",
          color: "#f3f4f6",
          fontSize: "11px",
          lineHeight: 1.35,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
      >
        <div style={{ color: "#d6b06c", fontWeight: 700, marginBottom: 4 }}>
          ARIA Debug · Preview
        </div>
        <div>{device} · {browser} · {mode}</div>
        <hr style={{ border: 0, borderTop: "1px solid rgba(255,255,255,0.1)", margin: "6px 0" }} />
        <div>Tap → listening: <b data-testid="aria-debug-tap-to-listening">{fmt(snap.tap_to_listening_ms)}</b></div>
        <div>Listening → speech end: <b data-testid="aria-debug-listening-to-end">{fmt(snap.listening_to_speech_end_ms)}</b></div>
        <div>TTS start: <b data-testid="aria-debug-tts-start">{snap.tts_start ? "yes" : "—"}</b></div>
        <div>TTS end: <b data-testid="aria-debug-tts-end">{snap.tts_end ? "yes" : "—"}</b></div>
        <div>TTS duration: <b data-testid="aria-debug-tts-duration">{fmt(snap.tts_duration_ms)}</b></div>
        <div>SR error: <b data-testid="aria-debug-error">{snap.recognition_error_code || "none"}</b></div>
        <hr style={{ border: 0, borderTop: "1px solid rgba(255,255,255,0.1)", margin: "6px 0" }} />
        <div style={{ color: "#d6b06c", fontWeight: 600, marginBottom: 3 }}>
          Event trace
          {snap.readiness_timeout_ms
            ? ` · readiness ${snap.readiness_timeout_ms}ms · retries ${snap.max_keep_alive_restarts ?? "?"}`
            : ""}
        </div>
        <div data-testid="aria-debug-trace" style={{ fontSize: "10px", lineHeight: 1.35 }}>
          {(!snap.trace || snap.trace.length === 0) ? (
            <div style={{ color: "rgba(255,255,255,0.4)" }}>—</div>
          ) : (
            snap.trace.map((e, i) => (
              <div key={i} data-testid={`aria-debug-trace-entry-${i}`}>
                <span style={{ color: "#9ca3af" }}>+{e.t}ms</span>{" "}
                <b>{e.event}</b>
              </div>
            ))
          )}
        </div>
        <div style={{ color: "rgba(255,255,255,0.55)", marginTop: 6, fontSize: "10px" }}>
          No transcript · No PHI · Numbers only
        </div>
      </div>
    </div>
  );
}
